import React from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { TFunction } from 'react-i18next';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from 'lodash';
import {
  Stack,
  List,
  Pagination,
  TextField,
  withTranslation,
  StyledContainer,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Pagination as PaginationManager,
  view,
  withAccess,
  errorBoundary,
  withInject,
  ConfigService,
  QueryParams,
  InternalListLink,
  PermissionSelect,
} from '@piwikpro/platform';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { AccessControlService, PermissionLevel } from '@piwikpro/access-control-crate';
import { App } from '@piwikpro/apps-crate';
import { permissionSelectOptions } from '../../../../permissionOptions';

interface InjectedProps {
  accessControl: AccessControlService
  userPreferences: any
  routerService: RouterService
  config: ConfigService
  appPermissionLevels: PermissionLevel[]
  Title: typeof React.Component
}

interface Props extends InjectedProps {
  app: App
  isLoading: boolean
  failed: boolean
  total: number
  permissions: string[]
  routeParams: QueryParams
  pathname: string
  t: TFunction
}

interface State {
  searchDebounce: null | DebouncedFunc<() => void>
  search: string
}

class UserGroups extends React.Component<Props, State> {
  state: State = {
    searchDebounce: null,
    search: '',
  }

  constructor(props) {
    super(props);

    this.handleLimitChange = this.handleLimitChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.renderPermissionsRow = this.renderPermissionsRow.bind(this);
    this.handleUpdatePermissions = this.handleUpdatePermissions.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  async componentDidMount() {
    await this.fetchPermissions();
  }

  async componentDidUpdate(prevProps: Props) {
    const { routeParams } = this.props;

    if (
      routeParams.page !== prevProps.routeParams.page
      || routeParams.limit !== prevProps.routeParams.limit
    ) {
      await this.fetchPermissions();
    }
  }

  async fetchPermissions() {
    const { accessControl, app, routeParams } = this.props;
    const { search } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    await accessControl.fetchUserGroupPermissionsForApp(app.id, {
      search,
      limit: pagination.getLimit(),
      offset: pagination.offset,
    });
  }

  async handleLimitChange(limit) {
    const {
      routerService,
      pathname,
      accessControl,
      app,
      userPreferences,
    } = this.props;
    const listPreference = new ListPreference(userPreferences.load(), '');
    const { search } = this.state;

    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    await accessControl.fetchUserGroupPermissionsForApp(app.id, {
      search,
      limit,
      offset: 0,
    });

    routerService.push({ to: buildPaginatedPath(pathname, 1, limit) });
  }

  async handlePageChange(offset, pagination) {
    const { search } = this.state;
    const {
      routerService,
      pathname,
      accessControl,
      app,
    } = this.props;

    await accessControl.fetchUserGroupPermissionsForApp(app.id, {
      search,
      limit: pagination.getLimit(),
      offset,
    });

    routerService.push({
      to: buildPaginatedPath(
        pathname,
        pagination.getPageFromOffset(offset),
        pagination.getLimit(),
      ),
    });
  }

  handleSearchChange(event) {
    const { searchDebounce } = this.state;
    const {
      accessControl,
      app,
      routeParams,
      routerService,
      pathname,
      config,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    if (searchDebounce) {
      searchDebounce.cancel();
    }

    const handleSearch = debounce(() => {
      const { search } = this.state;

      accessControl.fetchUserGroupPermissionsForApp(app.id, {
        search,
        offset: pagination.offset,
        limit: pagination.getLimit(),
      });
      routerService.push({ to: buildPaginatedPath(pathname, 1, pagination.getLimit()) });
    }, config.get('LIST_SEARCH_DELAY'));

    this.setState({
      searchDebounce: handleSearch,
      search: event.target.value,
    });

    handleSearch();
  }

  async handleUpdatePermissions(appId, groupId, { values, setSubmitting, resetForm }) {
    const { accessControl } = this.props;

    try {
      await accessControl.updateUserGroupPermissionsForApp(appId, groupId, values);
    } catch (err) {
      resetForm();
    }

    setSubmitting(false);
  }

  renderPermissionsRow(userGroup) {
    const { app, appPermissionLevels, t } = this.props;
    const userGroupAccess = userGroup.attributes.permission;

    return (
      <List.Item
        key={userGroup.id}
      >
        <InternalListLink
          to="/administration/user-groups/$groupId"
          params={{ groupId: userGroup.id }}
          label={userGroup.attributes.name}
        />
        <PermissionSelect
          initialValue={userGroupAccess}
          options={permissionSelectOptions(appPermissionLevels, t, 'app')}
          onSubmit={(values, api) => (this.handleUpdatePermissions(
            app.id,
            userGroup.id,
            {
              values,
              ...api,
            },
          ))}
        />
      </List.Item>
    );
  }

  render() {
    const {
      permissions,
      isLoading,
      failed,
      routeParams,
      config,
      t,
      total,
      Title,
    } = this.props;
    const {
      search,
    } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      permissions,
      total,
      routeParams,
    );

    return (
      <Title part={t('apps.details.title.permissions.user-groups')}>
        <Stack.Item fill>
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                value={search}
                name="search"
                icon="search"
                onChange={this.handleSearchChange}
                placeholder={t('user-groups.list.searchPlaceholder')}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 4,
                name: t('user-groups.list.column.group-name'),
                align: 'left',
              },
              {
                size: 1,
                name: t('user-groups.list.column.group-permission'),
                align: 'left',
              },
            ]}
            isLoading={isLoading}
            error={failed}
          >
            {
              !failed && permissions
                .map(userGroupWithPermissions =>
                  this.renderPermissionsRow(userGroupWithPermissions))
            }
          </List>
        </Stack.Item>
        {!failed && (
          <Stack.Item fill>
            <Pagination
              onLimitChange={this.handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={e => this.handlePageChange(e, pagination)}
            />
          </Stack.Item>
        )}
      </Title>
    );
  }
}

const UserGroupsConnected = withInject<InjectedProps>({
  config: 'config',
  appPermissionLevels: 'appPermissionLevels',
  userPreferences: 'userPreferences',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
  accessControl: 'AccessControlCrate.accessControl',
})(
  view({ role: 'OWNER' })(withAccess(withTranslation(['administration'])(
    errorBoundary(UserGroups),
  ))),
);

const UserGroupsWrapper = (props) => {
  const routeParams = useParams({
    from: '/_secured/administration/apps/$appId/permissions/user-groups/$page/$limit',
  });
  const { pathname } = useLocation();

  return (
    <UserGroupsConnected
      {...props}
      routeParams={routeParams}
      pathname={pathname}
    />
  );
};

export default UserGroupsWrapper;
